import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';
import CustomerQueue from './CustomerQueue';
import FirstPersonView from './FirstPersonView';
import GameHUD from './GameHUD';
import OnboardingOverlay from './OnboardingOverlay';
import TutorialMode from './TutorialMode';
import IdleManager from './IdleManager';
import AutoCafe from './AutoCafe';
import IdleUpgrades from './IdleUpgrades';
import AutoBaristaManager from './AutoBaristaManager';
import BaristaWorkforce from './BaristaWorkforce';
import { coffeeRecipes } from '../data/coffeeRecipes';

const { FiArrowLeft, FiPause, FiPlay, FiEye, FiGrid, FiSettings, FiHelpCircle, FiCpu, FiUsers } = FiIcons;

function GameScreen({ onNavigate }) {
  const { state, dispatch } = useGame();
  const [gameState, setGameState] = useState('playing');
  const [currentOrder, setCurrentOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [gameTime, setGameTime] = useState(120);
  const [currentScore, setCurrentScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [viewMode, setViewMode] = useState('first-person');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [customersServed, setCustomersServed] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showIdlePanel, setShowIdlePanel] = useState(false);

  // Check if this is the first time playing
  useEffect(() => {
    const hasPlayedBefore = localStorage.getItem('hasPlayedBaristaMaster');
    if (!hasPlayedBefore) {
      setShowOnboarding(true);
    }
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasPlayedBaristaMaster', 'true');
  };

  const handleStartTutorial = () => {
    setShowOnboarding(false);
    setShowTutorial(true);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    localStorage.setItem('hasPlayedBaristaMaster', 'true');
  };

  // Generate random customer orders with proper recipe mapping
  const generateCustomer = useCallback(() => {
    const availableRecipes = Object.keys(coffeeRecipes).filter(recipe => {
      const recipeData = coffeeRecipes[recipe];
      if (recipeData.category === 'matcha' && state.level < 4) return false;
      if (recipeData.difficulty > state.level) return false;
      return true;
    });

    const customerNames = ['Emma', 'Jake', 'Sofia', 'Mike', 'Luna', 'Alex', 'Zoe', 'Sam'];
    const moods = ['happy', 'neutral', 'impatient'];

    const randomRecipe = availableRecipes[Math.floor(Math.random() * availableRecipes.length)];
    const randomName = customerNames[Math.floor(Math.random() * customerNames.length)];
    const randomMood = moods[Math.floor(Math.random() * moods.length)];

    return {
      id: Date.now() + Math.random(),
      name: randomName,
      drink: randomRecipe,
      extras: [],
      mood: randomMood,
      patience: randomMood === 'impatient' ? 20 : randomMood === 'neutral' ? 30 : 40,
      maxPatience: randomMood === 'impatient' ? 20 : randomMood === 'neutral' ? 30 : 40,
      orderTime: Date.now()
    };
  }, [state.level]);

  // Auto-select first customer in first-person mode
  useEffect(() => {
    if (viewMode === 'first-person' && customers.length > 0 && !currentOrder) {
      setCurrentOrder(customers[0]);
    }
  }, [customers, currentOrder, viewMode]);

  // Game timer
  useEffect(() => {
    if (gameState === 'playing' && gameTime > 0 && !showOnboarding && !showTutorial) {
      const timer = setInterval(() => {
        setGameTime(prev => {
          if (prev <= 1) {
            setGameState('finished');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState, gameTime, showOnboarding, showTutorial]);

  // Customer generation
  useEffect(() => {
    if (gameState === 'playing' && !showOnboarding && !showTutorial) {
      const interval = setInterval(() => {
        if (customers.length < 4) {
          setCustomers(prev => {
            const newCustomer = generateCustomer();
            return [...prev, newCustomer];
          });
        }
      }, 3000 + Math.random() * 2000);

      return () => clearInterval(interval);
    }
  }, [gameState, customers.length, generateCustomer, showOnboarding, showTutorial]);

  // Customer patience countdown
  useEffect(() => {
    if (gameState === 'playing' && !showOnboarding && !showTutorial) {
      const interval = setInterval(() => {
        setCustomers(prev => {
          const updatedCustomers = prev.map(customer => ({
            ...customer,
            patience: Math.max(0, customer.patience - 1)
          })).filter(customer => {
            if (customer.patience <= 0) {
              if (currentOrder && currentOrder.id === customer.id) {
                setCurrentOrder(null);
              }
              return false;
            }
            return true;
          });

          if (viewMode === 'first-person' && (!currentOrder || !updatedCustomers.find(c => c.id === currentOrder.id))) {
            const nextCustomer = updatedCustomers.length > 0 ? updatedCustomers[0] : null;
            setCurrentOrder(nextCustomer);
          }

          return updatedCustomers;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [gameState, viewMode, currentOrder, showOnboarding, showTutorial]);

  const handleOrderComplete = (order, accuracy, feedback = null) => {
    const basePoints = 100;
    const accuracyBonus = Math.floor(accuracy * 100);
    const speedBonus = order.patience > 15 ? 50 : 0;
    const streakBonus = streak * 10;
    const difficultyBonus = coffeeRecipes[order.drink]?.difficulty * 20 || 0;
    const totalPoints = basePoints + accuracyBonus + speedBonus + streakBonus + difficultyBonus;

    setCurrentScore(prev => prev + totalPoints);
    setCustomersServed(prev => prev + 1);

    if (accuracy >= 0.8) {
      setStreak(prev => prev + 1);
      dispatch({ type: 'ADD_COINS', payload: Math.floor(totalPoints / 20) });
      if (accuracy >= 0.95) {
        dispatch({ type: 'UPDATE_STATS', payload: { perfectOrders: (state.stats.perfectOrders || 0) + 1 } });
      }
    } else {
      setStreak(0);
    }

    dispatch({ type: 'ADD_EXPERIENCE', payload: Math.floor(totalPoints / 10) });

    setCustomers(prev => {
      const newCustomers = prev.filter(c => c.id !== order.id);
      if (viewMode === 'first-person') {
        const nextCustomer = newCustomers.length > 0 ? newCustomers[0] : null;
        setCurrentOrder(nextCustomer);
      } else {
        setCurrentOrder(null);
      }
      return newCustomers;
    });
  };

  const handleCustomerNameChange = (newName) => {
    if (currentOrder && newName) {
      setCurrentOrder(prev => ({ ...prev, name: newName }));
      setCustomers(prev => prev.map(c => c.id === currentOrder.id ? { ...c, name: newName } : c));
    }
  };

  const handleGameEnd = () => {
    dispatch({ type: 'ADD_SCORE', payload: currentScore });
    dispatch({ 
      type: 'UPDATE_STATS', 
      payload: {
        drinksServed: (state.stats.drinksServed || 0) + customersServed,
        totalEarnings: (state.stats.totalEarnings || 0) + currentScore
      }
    });
  };

  useEffect(() => {
    if (gameState === 'finished') {
      handleGameEnd();
    }
  }, [gameState]);

  // Show onboarding first
  if (showOnboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
        <OnboardingOverlay
          onComplete={handleOnboardingComplete}
          onStartTutorial={handleStartTutorial}
        />
      </div>
    );
  }

  // Show tutorial if requested
  if (showTutorial) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
        <TutorialMode onComplete={handleTutorialComplete} />
      </div>
    );
  }

  if (gameState === 'finished') {
    return (
      <motion.div
        className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl text-center max-w-sm w-full">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-2xl sm:text-3xl font-bold text-amber-600 mb-4">Shift Complete!</h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-purple-50 p-3 rounded-xl">
              <p className="text-2xl font-bold text-purple-600">{currentScore}</p>
              <p className="text-sm text-purple-700">Final Score</p>
            </div>
            <div className="bg-green-50 p-3 rounded-xl">
              <p className="text-2xl font-bold text-green-600">{streak}</p>
              <p className="text-sm text-green-700">Best Streak</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{customersServed}</p>
              <p className="text-sm text-blue-700">Customers</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-xl">
              <p className="text-2xl font-bold text-yellow-600">{Math.floor(currentScore / 20)}</p>
              <p className="text-sm text-yellow-700">Coins</p>
            </div>
          </div>
          <motion.button
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 sm:px-8 py-3 rounded-2xl font-bold w-full"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onNavigate('menu')}
          >
            Back to Menu
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 relative">
      {/* Idle Manager - Handles offline progress */}
      <IdleManager />

      {/* Enhanced Header - Better visual hierarchy */}
      <div className="absolute top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between p-3 bg-white/95 backdrop-blur-md shadow-lg">
          {/* Left Controls */}
          <div className="flex items-center gap-2">
            <motion.button
              className="bg-gray-100 hover:bg-gray-200 p-2.5 rounded-xl shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onNavigate('menu')}
            >
              <SafeIcon icon={FiArrowLeft} className="text-lg text-gray-700" />
            </motion.button>
            <motion.button
              className="bg-gray-100 hover:bg-gray-200 p-2.5 rounded-xl shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setGameState(gameState === 'paused' ? 'playing' : 'paused')}
            >
              <SafeIcon icon={gameState === 'paused' ? FiPlay : FiPause} className="text-lg text-gray-700" />
            </motion.button>
          </div>

          {/* Center HUD - Compact and informative */}
          <div className="flex items-center gap-4 bg-gray-50 px-4 py-2 rounded-xl">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {Math.floor(gameTime / 60)}:{(gameTime % 60).toString().padStart(2, '0')}
              </div>
              <div className="text-xs text-gray-500">Time</div>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">{currentScore}</div>
              <div className="text-xs text-gray-500">Score</div>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">{streak}</div>
              <div className="text-xs text-gray-500">Streak</div>
            </div>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-2">
            <motion.button
              className={`p-2.5 rounded-xl shadow-md ${
                viewMode === 'first-person' ? 'bg-purple-500 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('first-person')}
            >
              <SafeIcon icon={FiEye} className="text-lg" />
            </motion.button>
            <motion.button
              className={`p-2.5 rounded-xl shadow-md ${
                viewMode === 'traditional' ? 'bg-purple-500 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('traditional')}
            >
              <SafeIcon icon={FiGrid} className="text-lg" />
            </motion.button>
            <motion.button
              className={`p-2.5 rounded-xl shadow-md ${
                showIdlePanel ? 'bg-purple-500 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowIdlePanel(!showIdlePanel)}
            >
              <SafeIcon icon={FiUsers} className="text-lg" />
            </motion.button>
            <motion.button
              className="bg-gray-100 hover:bg-gray-200 p-2.5 rounded-xl shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSettings(!showSettings)}
            >
              <SafeIcon icon={FiSettings} className="text-lg text-gray-700" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Enhanced Idle Panel - Now includes Auto Barista Manager */}
      <AnimatePresence>
        {showIdlePanel && (
          <motion.div
            className="absolute top-16 left-3 z-40 w-80 max-h-[80vh] overflow-y-auto space-y-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <AutoBaristaManager />
            <BaristaWorkforce />
            <AutoCafe />
            <IdleUpgrades />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            className="absolute top-16 right-3 z-40 bg-white rounded-2xl p-4 shadow-2xl border"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
          >
            <div className="space-y-3 w-48">
              <h3 className="font-bold text-gray-800 text-sm">Game Settings</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Sound Effects</span>
                  <div className="w-10 h-6 bg-gray-200 rounded-full relative">
                    <div className="w-4 h-4 bg-white rounded-full absolute top-1 left-1 shadow"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Haptic Feedback</span>
                  <div className="w-10 h-6 bg-green-500 rounded-full relative">
                    <div className="w-4 h-4 bg-white rounded-full absolute top-1 right-1 shadow"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Auto-Select Customer</span>
                  <div className="w-10 h-6 bg-green-500 rounded-full relative">
                    <div className="w-4 h-4 bg-white rounded-full absolute top-1 right-1 shadow"></div>
                  </div>
                </div>
              </div>
              <div className="border-t pt-3">
                <button className="flex items-center text-sm text-blue-600 hover:text-blue-700">
                  <SafeIcon icon={FiHelpCircle} className="mr-2" />
                  Tutorial
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Game Content */}
      <div className="pt-16 h-screen overflow-hidden">
        {viewMode === 'first-person' ? (
          <FirstPersonView
            currentOrder={currentOrder}
            onOrderComplete={handleOrderComplete}
            gameState={gameState}
            unlockedItems={state.unlockedItems}
            onCustomerNameChange={handleCustomerNameChange}
          />
        ) : (
          <div className="h-full grid lg:grid-cols-3 gap-4 p-4">
            {/* Customer Queue - Better mobile layout */}
            <div className="lg:col-span-1 h-full">
              <CustomerQueue
                customers={customers}
                currentOrder={currentOrder}
                onSelectOrder={setCurrentOrder}
              />
            </div>
            {/* Drink Making Area - Optimized space */}
            <div className="lg:col-span-2 h-full">
              <FirstPersonView
                currentOrder={currentOrder}
                onOrderComplete={handleOrderComplete}
                gameState={gameState}
                unlockedItems={state.unlockedItems}
                onCustomerNameChange={handleCustomerNameChange}
              />
            </div>
          </div>
        )}
      </div>

      {/* Customer Queue Indicator for First-Person Mode */}
      {viewMode === 'first-person' && customers.length > 0 && (
        <div className="absolute bottom-4 right-4 z-30">
          <motion.div
            className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-xl"
            whileHover={{ scale: 1.05 }}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {customers.length}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Waiting</p>
                <p className="text-xs text-gray-500">
                  {customers.length === 1 ? '1 customer' : `${customers.length} customers`}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Pause Overlay - Enhanced design */}
      <AnimatePresence>
        {gameState === 'paused' && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-sm w-full mx-4"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              <div className="text-6xl mb-4">⏸️</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Game Paused</h3>
              <div className="space-y-3">
                <motion.button
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-3 rounded-2xl font-bold w-full"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setGameState('playing')}
                >
                  Resume Game
                </motion.button>
                <motion.button
                  className="bg-gray-100 text-gray-600 px-8 py-3 rounded-2xl font-bold w-full"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onNavigate('menu')}
                >
                  Main Menu
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Performance Indicator */}
      {streak > 0 && (
        <motion.div
          className="absolute top-20 left-1/2 transform -translate-x-1/2 z-30"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 rounded-full shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-lg">🔥</span>
              <span className="font-bold">{streak} Streak!</span>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default GameScreen;